import {
  HONORS,
  KIND_COUNT,
  PAIR_SIZE,
  RANKS,
  SET_SIZE,
  SETS_TO_WIN,
  SUITS,
  SUIT_KIND_COUNT,
  kindIndex,
  type Tile,
} from './types.js';
import type { Ruleset } from './ruleset/types.js';

// A run starting at rank r needs r+2 ≤ 9, i.e. an in-suit offset of at most 6.
const MAX_RUN_START_OFFSET = RANKS.length - SET_SIZE;

/** Total tiles in any complete winning hand: 4 sets × 3 tiles + a pair. */
const WINNING_HAND_SIZE = SETS_TO_WIN * SET_SIZE + PAIR_SIZE;

/** Seven Pairs: exactly this many distinct kinds, each held twice. */
const SEVEN_PAIRS_KIND_COUNT = 7;

/** The 13 "terminal/honor" kinds: rank-1 and rank-9 of each suit, plus every honor. */
const TERMINAL_HONOR_KIND_COUNT = SUITS.length * 2 + HONORS.length;

type KindCounts = number[];

function countsByKindIndex(tiles: readonly Tile[]): KindCounts {
  const counts: KindCounts = new Array<number>(KIND_COUNT).fill(0);
  for (const tile of tiles) {
    const i = kindIndex(tile);
    counts[i] = (counts[i] ?? 0) + 1;
  }
  return counts;
}

function countAt(counts: KindCounts, i: number): number {
  return counts[i] ?? 0;
}

function add(counts: KindCounts, i: number, delta: number): void {
  counts[i] = countAt(counts, i) + delta;
}

/** Kind `i` may start a run: suit tile with rank ≤ 7. Honors never form runs;
 * cross-suit runs are impossible because offsets ≤ 6 keep i+1/i+2 in-suit. */
function canStartRun(i: number): boolean {
  return i < SUIT_KIND_COUNT && i % RANKS.length <= MAX_RUN_START_OFFSET;
}

/** Backtracking: can `counts` be fully consumed as exactly `need` sets? */
function canFormMelds(counts: KindCounts, need: number): boolean {
  let first = 0;
  while (first < KIND_COUNT && countAt(counts, first) === 0) first++;
  if (first === KIND_COUNT) return need === 0;
  if (need === 0) return false;

  if (countAt(counts, first) >= SET_SIZE) {
    add(counts, first, -SET_SIZE);
    const ok = canFormMelds(counts, need - 1);
    add(counts, first, SET_SIZE);
    if (ok) return true;
  }

  if (canStartRun(first) && countAt(counts, first + 1) > 0 && countAt(counts, first + 2) > 0) {
    add(counts, first, -1);
    add(counts, first + 1, -1);
    add(counts, first + 2, -1);
    const ok = canFormMelds(counts, need - 1);
    add(counts, first, 1);
    add(counts, first + 1, 1);
    add(counts, first + 2, 1);
    if (ok) return true;
  }

  return false;
}

/**
 * True iff `concealed` plus `meldCount` open melds is a winning hand:
 * (4 − meldCount) concealed sets + 1 pair, with the concealed tile count
 * exactly (4 − meldCount) × 3 + 2. Tries every kind with ≥ 2 copies as the
 * pair, then backtracks over triplets and runs for the rest.
 */
function isStandardWinningHand(concealed: readonly Tile[], meldCount: number): boolean {
  const setsNeeded = SETS_TO_WIN - meldCount;
  if (setsNeeded < 0 || concealed.length !== setsNeeded * SET_SIZE + PAIR_SIZE) {
    return false;
  }
  const counts = countsByKindIndex(concealed);
  for (let i = 0; i < KIND_COUNT; i++) {
    if (countAt(counts, i) >= PAIR_SIZE) {
      add(counts, i, -PAIR_SIZE);
      const ok = canFormMelds(counts, setsNeeded);
      add(counts, i, PAIR_SIZE);
      if (ok) return true;
    }
  }
  return false;
}

/**
 * True iff `concealed` is exactly 14 tiles forming 7 distinct kinds, each
 * held exactly twice. An alternate win shape (Classical only) — never a
 * sub-case of the standard 4-sets-and-a-pair shape.
 */
export function isSevenPairs(concealed: readonly Tile[]): boolean {
  if (concealed.length !== WINNING_HAND_SIZE) return false;
  const counts = countsByKindIndex(concealed);
  let distinctKinds = 0;
  for (let i = 0; i < KIND_COUNT; i++) {
    const count = countAt(counts, i);
    if (count === 0) continue;
    if (count !== PAIR_SIZE) return false;
    distinctKinds++;
  }
  return distinctKinds === SEVEN_PAIRS_KIND_COUNT;
}

/** Kind `i` is a terminal (rank 1 or 9) or an honor. */
function isTerminalOrHonorKind(i: number): boolean {
  if (i >= SUIT_KIND_COUNT) return true;
  const rankOffset = i % RANKS.length;
  return rankOffset === 0 || rankOffset === RANKS.length - 1;
}

/**
 * True iff `concealed` is exactly 14 tiles consisting of all 13
 * terminal/honor kinds, with exactly one of them held twice and the rest
 * held once. An alternate win shape (Classical only).
 */
export function isThirteenOrphans(concealed: readonly Tile[]): boolean {
  if (concealed.length !== WINNING_HAND_SIZE) return false;
  const counts = countsByKindIndex(concealed);
  let pairKinds = 0;
  let singleKinds = 0;
  for (let i = 0; i < KIND_COUNT; i++) {
    const count = countAt(counts, i);
    if (count === 0) continue;
    if (!isTerminalOrHonorKind(i)) return false;
    if (count === 1) {
      singleKinds++;
    } else if (count === PAIR_SIZE) {
      pairKinds++;
    } else {
      return false;
    }
  }
  return pairKinds === 1 && singleKinds === TERMINAL_HONOR_KIND_COUNT - 1;
}

/**
 * True iff `concealed` plus `meldCount` open melds is a winning hand. The
 * standard 4-sets-and-a-pair shape is always checked first (every ruleset
 * wins this way). If that fails and `ruleset.alternateWinShapes` is set,
 * Seven Pairs and Thirteen Orphans are tried too — both require a fully
 * concealed hand, so they only apply when `meldCount === 0`.
 */
export function isWinningHand(
  concealed: readonly Tile[],
  meldCount: number,
  ruleset?: Ruleset,
): boolean {
  if (isStandardWinningHand(concealed, meldCount)) return true;
  if (ruleset?.alternateWinShapes === true && meldCount === 0) {
    return isSevenPairs(concealed) || isThirteenOrphans(concealed);
  }
  return false;
}
